/**
 * About Page - Clinafy Institutional Website
 *
 * Company story, mission, values, and team information.
 */

import React from 'react';
import { useSEO, SEO_CONFIG } from '../../hooks/useSEO';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  alpha,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

// Icons
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FavoriteIcon from '@mui/icons-material/Favorite';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import GroupsIcon from '@mui/icons-material/Groups';
import VerifiedIcon from '@mui/icons-material/Verified';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

// Components
import Navbar from '../../components/landing/Navbar';
import Footer from '../../components/landing/Footer';
import { landingColors, landingGradients, landingShadows } from '../../theme/landingTheme';

// ============================================================================
// Data
// ============================================================================

const timeline = [
  {
    year: '2023',
    title: 'A Ideia',
    description: 'Uma profissional de saúde mental perdeu anotações importantes de um paciente. Percebeu que precisava de algo melhor que planilhas e papéis.',
    icon: <LightbulbIcon />,
  },
  {
    year: '2024',
    title: 'O Desenvolvimento',
    description: 'Parceria com equipe de tecnologia. Meses de pesquisa e entrevistas com profissionais de saúde mental para entender suas necessidades.',
    icon: <RocketLaunchIcon />,
  },
  {
    year: '2025',
    title: 'O Lançamento',
    description: 'Clinafy nasce com missão clara: libertar profissionais da burocracia para que possam focar no que importa.',
    icon: <VerifiedIcon />,
  },
];

const values = [
  {
    icon: <LightbulbIcon sx={{ fontSize: 32 }} />,
    title: 'Simplicidade',
    description: 'Tecnologia complexa não precisa ser complicada de usar. Buscamos a interface mais intuitiva possível.',
  },
  {
    icon: <VerifiedIcon sx={{ fontSize: 32 }} />,
    title: 'Segurança',
    description: 'Dados de pacientes são sagrados. Investimos pesado em criptografia e conformidade.',
  },
  {
    icon: <FavoriteIcon sx={{ fontSize: 32 }} />,
    title: 'Empatia',
    description: 'Entendemos as dores dos profissionais porque construímos junto com eles.',
  },
  {
    icon: <RocketLaunchIcon sx={{ fontSize: 32 }} />,
    title: 'Inovação',
    description: 'Estamos sempre buscando formas de melhorar a experiência com tecnologia de ponta.',
  },
];

const stats = [
  { value: '+500', label: 'Profissionais Ativos', icon: <GroupsIcon /> },
  { value: '+50.000', label: 'Consultas Realizadas', icon: <TrendingUpIcon /> },
  { value: '99.9%', label: 'Uptime do Sistema', icon: <VerifiedIcon /> },
  { value: '<2h', label: 'Tempo de Resposta', icon: <SupportAgentIcon /> },
];

// ============================================================================
// Component
// ============================================================================

const AboutPage: React.FC = () => {
  const navigate = useNavigate();

  // SEO - Dynamic meta tags
  useSEO(SEO_CONFIG.sobre);

  return (
    <Box sx={{ bgcolor: landingColors.background.default }}>
      <Navbar />

      <Box component="main" id="main-content">
      {/* ================================================================== */}
      {/* HERO SECTION */}
      {/* ================================================================== */}
      <Box
        sx={{
          background: landingGradients.hero,
          pt: { xs: 8, md: 10 },
          pb: { xs: 8, md: 10 },
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h1"
            align="center"
            sx={{
              color: landingColors.neutral.white,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 700,
              mb: 3,
            }}
          >
            Nascemos da frustração de uma profissional de saúde mental
          </Typography>
          <Typography
            align="center"
            sx={{
              color: alpha(landingColors.neutral.white, 0.9),
              fontSize: { xs: '1.1rem', md: '1.25rem' },
              lineHeight: 1.7,
            }}
          >
            E da vontade de um time de tecnologia de resolver problemas reais.
            Acreditamos que profissionais de saúde mental devem passar menos tempo com papelada
            e mais tempo cuidando de pessoas.
          </Typography>
        </Container>
      </Box>

      {/* ================================================================== */}
      {/* TIMELINE SECTION */}
      {/* ================================================================== */}
      <Box sx={{ py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            align="center"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '2rem', md: '2.5rem' },
              color: landingColors.neutral.gray900,
              mb: 6,
            }}
          >
            Nossa História
          </Typography>

          <Grid container spacing={4}>
            {timeline.map((item, index) => (
              <Grid key={index} size={{ xs: 12, md: 4 }}>
                <Card
                  sx={{
                    height: '100%',
                    p: 4,
                    borderRadius: 3,
                    bgcolor: '#FFFFFF',
                    border: `1px solid ${landingColors.neutral.gray200}`,
                    boxShadow: 'none',
                    position: 'relative',
                    '&:hover': {
                      boxShadow: landingShadows.card,
                      borderColor: landingColors.primary.light,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      bgcolor: alpha(landingColors.primary.main, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: landingColors.primary.main,
                      mb: 3,
                    }}
                  >
                    {item.icon}
                  </Box>

                  <Typography
                    sx={{
                      color: landingColors.primary.main,
                      fontWeight: 700,
                      fontSize: '1.5rem',
                      mb: 1,
                    }}
                  >
                    {item.year}
                  </Typography>

                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      color: landingColors.neutral.gray900,
                      mb: 2,
                    }}
                  >
                    {item.title}
                  </Typography>

                  <Typography
                    sx={{
                      color: landingColors.neutral.gray600,
                      lineHeight: 1.7,
                    }}
                  >
                    {item.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ================================================================== */}
      {/* MISSION VISION VALUES */}
      {/* ================================================================== */}
      <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: landingColors.background.alt }}>
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            {/* Mission */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card
                sx={{
                  height: '100%',
                  p: 4,
                  borderRadius: 3,
                  bgcolor: '#FFFFFF',
                  border: `1px solid ${landingColors.neutral.gray200}`,
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: landingShadows.card,
                    borderColor: landingColors.primary.light,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    bgcolor: alpha(landingColors.primary.main, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: landingColors.primary.main,
                    mb: 3,
                  }}
                >
                  <LightbulbIcon sx={{ fontSize: 28 }} />
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 600, mb: 2, color: landingColors.neutral.gray900 }}>
                  Nossa Missão
                </Typography>
                <Typography sx={{ fontSize: '1.1rem', lineHeight: 1.7, color: landingColors.neutral.gray600 }}>
                  Libertar profissionais de saúde mental da burocracia para que possam focar 100% em seus pacientes.
                  Queremos que cada profissional tenha acesso a tecnologia de ponta, independente
                  do tamanho da sua clínica.
                </Typography>
              </Card>
            </Grid>

            {/* Vision */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card
                sx={{
                  height: '100%',
                  p: 4,
                  borderRadius: 3,
                  bgcolor: '#FFFFFF',
                  border: `1px solid ${landingColors.neutral.gray200}`,
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: landingShadows.card,
                    borderColor: landingColors.primary.light,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    bgcolor: alpha(landingColors.primary.main, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: landingColors.primary.main,
                    mb: 3,
                  }}
                >
                  <VisibilityIcon sx={{ fontSize: 28 }} />
                </Box>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 600, mb: 2, color: landingColors.neutral.gray900 }}
                >
                  Nossa Visão
                </Typography>
                <Typography
                  sx={{
                    fontSize: '1.1rem',
                    lineHeight: 1.7,
                    color: landingColors.neutral.gray600,
                  }}
                >
                  Ser a plataforma mais amada por profissionais de saúde mental no Brasil.
                  Queremos que quando alguém pensar em gestão de clínica de saúde mental,
                  pense imediatamente em Clinafy.
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ================================================================== */}
      {/* VALUES */}
      {/* ================================================================== */}
      <Box sx={{ py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            align="center"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '2rem', md: '2.5rem' },
              color: landingColors.neutral.gray900,
              mb: 6,
            }}
          >
            Nossos Valores
          </Typography>

          <Grid container spacing={3}>
            {values.map((value, index) => (
              <Grid key={index} size={{ xs: 12, sm: 6, md: 3 }}>
                <Card
                  sx={{
                    height: '100%',
                    p: 3,
                    borderRadius: 3,
                    bgcolor: '#FFFFFF',
                    border: `1px solid ${landingColors.neutral.gray200}`,
                    boxShadow: 'none',
                    textAlign: 'center',
                    '&:hover': {
                      boxShadow: landingShadows.card,
                      borderColor: landingColors.primary.light,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 70,
                      height: 70,
                      borderRadius: '50%',
                      bgcolor: alpha(landingColors.primary.main, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: landingColors.primary.main,
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    {value.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: landingColors.neutral.gray900 }}>
                    {value.title}
                  </Typography>
                  <Typography
                    sx={{
                      color: landingColors.neutral.gray600,
                      fontSize: '0.9rem',
                      lineHeight: 1.6,
                    }}
                  >
                    {value.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ================================================================== */}
      {/* STATS SECTION */}
      {/* ================================================================== */}
      <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: landingColors.background.alt }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            align="center"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '2rem', md: '2.5rem' },
              color: landingColors.neutral.gray900,
              mb: 6,
            }}
          >
            Clinafy em Números
          </Typography>

          <Grid container spacing={4}>
            {stats.map((stat, index) => (
              <Grid key={index} size={{ xs: 6, md: 3 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      bgcolor: alpha(landingColors.primary.main, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: landingColors.primary.main,
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    {stat.icon}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: { xs: '2rem', md: '2.5rem' },
                      fontWeight: 700,
                      color: landingColors.primary.main,
                      lineHeight: 1,
                      mb: 1,
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography
                    sx={{
                      color: landingColors.neutral.gray600,
                      fontSize: '0.9rem',
                    }}
                  >
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ================================================================== */}
      {/* FINAL CTA */}
      {/* ================================================================== */}
      <Box
        sx={{
          background: landingGradients.heroDark,
          py: { xs: 8, md: 10 },
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography
            variant="h2"
            sx={{
              color: landingColors.neutral.white,
              fontWeight: 600,
              fontSize: { xs: '2rem', md: '2.5rem' },
              mb: 2,
            }}
          >
            Faça parte dessa história
          </Typography>
          <Typography
            sx={{
              color: alpha(landingColors.neutral.white, 0.8),
              fontSize: '1.1rem',
              mb: 4,
            }}
          >
            Junte-se a centenas de profissionais que já transformaram suas clínicas.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/signup?trial=true')}
            sx={{
              bgcolor: landingColors.neutral.white,
              color: landingColors.primary.main,
              fontWeight: 600,
              px: 5,
              py: 1.5,
              '&:hover': {
                bgcolor: landingColors.neutral.gray100,
              },
            }}
          >
            Começar Teste Gratuito
          </Button>
        </Container>
      </Box>

      </Box>

      <Footer />
    </Box>
  );
};

export default AboutPage;
